<script generics="T" lang="ts">
	import Disabler from '../../disabler/Disabler.svelte'
	import Icon from '../../icon/Icon.svelte'
	import SkeletonLoader from '../../loaders/SkeletonLoader.svelte'
	import ErrorLabel from '../errorLabel/ErrorLabel.svelte'
	import SelectSearch from '../selectSearch/SelectSearch.svelte'
	import { searchInText } from '../selectSearch/searchInText'
	import NativeHiddenSelect from './NativeHiddenSelect.svelte'
	import SelectAll from './SelectAll.svelte'
	import SelectKeyboardControl from './SelectKeyboardControl.svelte'
	import SelectVirtualList from './SelectVirtualList.svelte'
	import SelectedItems from './SelectedItems.svelte'
	import { isOptionOfValue } from './select'
	import { type SelectOption } from './selectOption'
	import { _ } from '@repo/i18n'
	import { Dropdown, dropdownOpen, getContextErrors } from '@repo/ui'
	import { DropdownBox } from '@repo/ui'
	import { Label } from '@repo/ui'
	import { Check, ChevronUpDown } from '@steeze-ui/heroicons'
	import { createEventDispatcher } from 'svelte'
	import { run } from 'svelte/legacy'

	interface Props {
		options?: SelectOption<T>[]
		values?: T[]
		value?: T
		name: string
		label?: string
		hideLabel?: boolean
		sideLabel?: boolean
		placeholder?: string | null
		required?: boolean
		hideRequired?: boolean
		hideError?: boolean
		disabled?: boolean
		showSelectAll?: boolean
		multiple?: boolean
		showAllPlaceholder?: boolean
		small?: boolean
		help?: string | null
		allowOnlyOptionValues?: boolean
		opened?: boolean
		loading?: boolean
		tooltip?: string | null
		itemHeight?: number
		autocomplete?: boolean
		searchText?: string | null
		maxSelections?: number
		onSearchTextChange?: () => void
		onChange?: (values: T[]) => void
		labelSlot?: import('svelte').Snippet
		placeholderSlot?: import('svelte').Snippet<[{ opened: boolean; onclick: () => void }]>
		optionSlot?: import('svelte').Snippet<
			[{ option: SelectOption<T>; value: T; checked: boolean; placeholder: boolean }]
		>
		children?: import('svelte').Snippet
	}

	let {
		options = $bindable([]),
		values = $bindable([]),
		value = $bindable(),
		name,
		label = '',
		hideLabel = false,
		sideLabel = false,
		placeholder: placeholderText = null,
		required = false,
		hideRequired = false,
		hideError = false,
		disabled = false,
		showSelectAll = false,
		multiple = false,
		showAllPlaceholder = true,
		small = false,
		help = null,
		allowOnlyOptionValues = false,
		opened = $bindable(false),
		loading = false,
		tooltip = null,
		itemHeight = 44,
		autocomplete = false,
		searchText = $bindable(null),
		maxSelections = 150,
		onSearchTextChange = null,
		onChange = null,
		labelSlot,
		placeholderSlot,
		optionSlot,
		children,
	}: Props = $props()

	const dispatch = createEventDispatcher()

	function setOption(option: SelectOption<T>) {
		if (value !== option.value) {
			value = option.value
			values = [option.value]
			dispatch('change', value)

			handleChange(values)
		}
		opened = false
		$dropdownOpen = false
	}

	const errors = getContextErrors()

	function handleChange(values: T[]) {
		if (values.length > maxSelections) {
			errors.upsert(name, {
				text: $_('error.maxItemsLimitReached', { values: { max: maxSelections } }),
			})
		} else {
			errors.upsert(name, null)
			onChange?.(values)
		}
	}

	function handleRemoveOption(event: CustomEvent) {
		toggleOption(event.detail)
	}

	function handleRemoveValue(event: CustomEvent) {
		removeValue(event.detail)
	}

	function handleRemoveAll() {
		// TODO: use "preselect" instead
		values = options.filter((option) => option.disabled).map((option) => option.value)
		handleChange(values)
	}

	function removeValue(value: T) {
		values = values.filter((v) => v !== value)
		dispatch('remove', value)

		handleChange(values)
	}

	function toggleOption(option: SelectOption<T>) {
		if (values === null) {
			values = []
		}

		let index = values.findIndex((value) => isOptionOfValue(option, value))
		if (index !== -1) {
			const value = values[index]
			values.splice(index, 1)
			values = values
			option.used = false

			dispatch('remove', value)
		} else {
			values.push(option.value)
			values = values

			dispatch('insert', option.value)
		}

		handleChange(values)
	}

	function filterValuesOnOptionsChange(options: SelectOption<T>[]) {
		if (values && values.length > 0) {
			values = values.filter((value) =>
				options.some((option) => JSON.stringify(option.value) === JSON.stringify(value)),
			)
		}

		if (
			value &&
			!options.some((option) => JSON.stringify(option.value) === JSON.stringify(value))
		) {
			value = null
		}
	}

	function preselect() {
		if (required && options.length === 1 && !value && values.length === 0) {
			setOption(options[0])
		}
	}

	function handleNativeChange(event: Event) {
		const target = event.target as HTMLSelectElement
		value = target.value as T
	}

	let focusIndex = $state(-1)
	let filteredOptions = $derived(
		(searchText && !autocomplete
			? options.filter((option) => searchInText(searchText, option.label))
			: options
		).filter((option) => !option.hidden),
	)
	run(() => {
		if (value) {
			values = [value]
		} else if (!multiple && value === null) {
			values = []
		}
	})
	run(() => {
		if (allowOnlyOptionValues && options) {
			filterValuesOnOptionsChange(options)
		}
	})
	run(() => {
		if (!loading) {
			preselect()
		}
	})
</script>

<Disabler enable={!disabled}>
	<div
		class="relative {sideLabel ? 'flex w-full flex-1 items-center gap-2' : ''}"
		class:z-20={opened}
	>
		{#if !hideLabel && label}
			<Label {name} {label} required={required && !hideRequired} {tooltip}>
				{#snippet action()}
					<span class="ml-auto flex text-sm font-normal">
						{@render labelSlot?.()}
					</span>
				{/snippet}
			</Label>
		{/if}

		{#if help}
			<p class="text-sm">
				{help}
			</p>

			<div class="mt-1"></div>
		{/if}

		<div class="relative {sideLabel ? 'flex-1' : ''}">
			{#if !loading}
				<NativeHiddenSelect
					{name}
					{disabled}
					{multiple}
					onChange={handleNativeChange}
					{options}
					{required}
					{values}
				/>
			{/if}
			<Dropdown portal bind:opened>
				{#snippet placeholder({ onclick })}
					{#if placeholderSlot}{@render placeholderSlot({ onclick, opened })}{:else}
						<button id={name} class="relative w-full" {disabled} {onclick} type="button">
							<div
								class="pointer-events-none absolute {!small
									? 'right-2'
									: 'right-0.5'} flex h-full items-center text-gray-400"
							>
								<Icon src={ChevronUpDown} />
							</div>
							<div
								class="input-colors flex truncate {!small
									? 'min-h-[42px]'
									: ''} shadow-input w-full appearance-none flex-wrap items-center gap-1 rounded-md border border-gray-200 py-1 pr-6 pl-1 text-sm"
							>
								{#if values && values.length > 0}
									{#if loading}
										<SkeletonLoader />
									{:else}
										<SelectedItems
											{autocomplete}
											{multiple}
											{options}
											{values}
											on:removeOption={handleRemoveOption}
											on:removeValue={handleRemoveValue}
											on:removeAll={handleRemoveAll}
										>
											{#snippet selectedOptionSlot({ option, value })}
												{#if optionSlot}
													{@render optionSlot({
														placeholder: true,
														checked: true,
														option,
														value,
													})}
												{:else}
													<div class="px-2 text-left text-sm">
														{option?.label || value}
													</div>
												{/if}
											{/snippet}
										</SelectedItems>
									{/if}
								{:else if !required && multiple && showAllPlaceholder}
									<div
										class="bg-primary-100 text-primary-500 inline-flex items-center rounded-xs px-2 py-1"
									>
										{$_('multiselect.all')}
									</div>
								{:else if placeholderText}
									<span class="px-2 text-base text-gray-400 {small ? 'text-sm' : ''}">
										{placeholderText}
									</span>
								{/if}
							</div>
						</button>
					{/if}
				{/snippet}

				<DropdownBox full portal>
					<div>
						{#if options?.length > 10 || autocomplete}
							<SelectSearch oninput={onSearchTextChange} bind:value={searchText} />
						{/if}
						{#if options?.length > 2 && multiple && showSelectAll}
							<SelectAll onChange={(values) => handleChange(values)} {options} bind:values />
						{/if}
						{#if filteredOptions.length}
							<SelectKeyboardControl lastItemIndex={filteredOptions.length - 1} bind:focusIndex>
								<SelectVirtualList itemCount={filteredOptions.length} {itemHeight}>
									{#snippet children({ index, style })}
										{@const option = filteredOptions[index]}
										{@const checked =
											values && values.some((value) => isOptionOfValue(option, value))}
										{@const unavailable = option.used && !checked}
										{#if !option.disabled}
											<!-- svelte-ignore a11y_mouse_events_have_key_events -->
											<button
												name={option.key ? option.key(option.value) : option.value?.toString()}
												{style}
												class="flex w-full justify-between p-3 text-sm focus:outline-hidden {focusIndex ===
												index
													? 'bg-gray-100 dark:bg-gray-600'
													: ''} {unavailable ? 'opacity-50' : ''}"
												disabled={unavailable}
												onclick={unavailable
													? null
													: () => (multiple ? toggleOption(option) : setOption(option))}
												onmouseover={unavailable
													? null
													: () => {
															focusIndex = index
														}}
												type="button"
											>
												<span class="flex truncate">
													{#if optionSlot}
														{@render optionSlot({ placeholder: false, checked, option, value })}
													{:else}
														{option.label}
													{/if}
												</span>
												<span class="text-primary-500 {checked ? 'flex' : 'hidden'}">
													<Icon src={Check} />
												</span>
											</button>
										{/if}
									{/snippet}
								</SelectVirtualList>
							</SelectKeyboardControl>
						{:else if !onSearchTextChange || searchText?.length > 0}
							<div class="flex w-full justify-between p-3 text-sm">
								{#if loading}
									<SkeletonLoader />
								{:else}
									<span>
										{$_('select.noItemsFound')}
									</span>
								{/if}
							</div>
						{/if}
					</div>
				</DropdownBox>
			</Dropdown>
		</div>

		{@render children?.()}
	</div>
</Disabler>

{#if !hideError}
	<ErrorLabel {name} />
{/if}
